import { useLayoutEffect, useMemo, useRef } from 'react'
import type { ChangeEvent } from 'react'
import {
  formatAmount,
  formatAmountLive,
  parseAmount,
} from '../../utils/amountFormatter'
import type { CT600TaxCompForm } from '../../types/ct600Filing'

function CurrencyInput({
  label,
  value,
  onChange,
  readOnly = false,
  highlight = false,
  helpText,
}: {
  label: string
  value: string
  onChange: (val: string) => void
  readOnly?: boolean
  highlight?: boolean
  helpText?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const cursorRef = useRef<number>(0)
  const displayValue = readOnly
    ? value
      ? formatAmount(value)
      : ''
    : value
      ? formatAmountLive(value)
      : ''

  useLayoutEffect(() => {
    const el = inputRef.current
    if (el && document.activeElement === el) {
      el.setSelectionRange(cursorRef.current, cursorRef.current)
    }
  }, [displayValue])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.target
    const pos = el.selectionStart || 0
    let digitsBefore = 0
    for (let i = 0; i < pos; i++) {
      if (el.value[i] !== ',') digitsBefore++
    }
    const raw = parseAmount(el.value)
    const formatted = raw ? formatAmountLive(raw) : ''
    let newPos = formatted.length
    let count = 0
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== ',') count++
      if (count === digitsBefore) {
        newPos = i + 1
        break
      }
    }
    cursorRef.current = newPos
    onChange(raw)
  }

  return (
    <div>
      <label className='mb-2 block text-sm font-medium text-gray-900'>
        {label}
      </label>
      {helpText && <p className='mb-1.5 text-xs text-gray-500'>{helpText}</p>}
      <div className='relative'>
        <span className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400'>
          £
        </span>
        <input
          ref={inputRef}
          type='text'
          value={displayValue}
          onChange={handleChange}
          onBlur={e => {
            const clean = parseAmount(e.target.value)
            if (clean) {
              const num = parseFloat(clean)
              if (!isNaN(num)) onChange(num.toFixed(2))
            }
          }}
          readOnly={readOnly}
          placeholder='0.00'
          className={`w-full rounded-lg border px-4 py-2.5 pl-8 text-sm text-right ${
            readOnly
              ? 'border-gray-200 bg-gray-50 text-gray-600'
              : highlight
                ? 'border-blue-300 bg-blue-50 font-semibold text-blue-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                : 'border-gray-300 text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
          }`}
        />
      </div>
    </div>
  )
}

interface Props {
  taxComp: CT600TaxCompForm
  profitBeforeTax: number
  onTaxCompChange: (update: CT600TaxCompForm) => void
}

export default function CT600StepTaxComp({
  taxComp,
  profitBeforeTax,
  onTaxCompChange,
}: Props) {
  const p = (v: string) => parseFloat(v) || 0

  const calc = useMemo(() => {
    // Add-backs are added straight into trading profit (depreciation /
    // entertainment / any-other).
    const addBacks =
      p(taxComp.depreciation) +
      p(taxComp.entertainmentDisallowable) +
      p(taxComp.otherAddBacks)

    // Mirror the backend's CT600 ordering from `recalculateTaxComputationTotals`
    // so the preview number matches what HMRC actually receives:
    //
    //   tradingProfitLoss = profitBeforeTax + addBacks - capitalAllowances
    //                        - otherDeductions
    //   totalProfits      = max(0, tradingProfitLoss) + bankInterest
    //     (interest is non-trading INCOME in CT/5, not a deduction)
    //   afterLosses       = max(0, totalProfits - lossesBroughtForward)
    //   adjusted          = max(0, afterLosses - charitableDonations)
    //
    // The max(0, …) clamps match HMRC's rule that you can't end up with a
    // negative chargeable profit — losses just zero it out.
    const tradingProfitLoss =
      profitBeforeTax +
      addBacks -
      p(taxComp.capitalAllowances) -
      p(taxComp.otherDeductions)
    const totalProfits =
      Math.max(0, tradingProfitLoss) + p(taxComp.bankInterest)
    const afterLosses = Math.max(
      0,
      totalProfits - p(taxComp.lossesBroughtForward)
    )
    const adjustedProfit = Math.max(
      0,
      afterLosses - p(taxComp.charitableDonations)
    )

    // 'Total Deductions' shown to the user — a friendly sum of the items in
    // the "Deduct from Profit" block. NOT the actual delta because bank
    // interest behaves as income in CT/5 ordering; we keep this as a visual
    // aggregate of what the user typed in the block, not as the math driver.
    const deductions =
      p(taxComp.capitalAllowances) +
      p(taxComp.bankInterest) +
      p(taxComp.lossesBroughtForward) +
      p(taxComp.charitableDonations) +
      p(taxComp.otherDeductions)

    return { addBacks, deductions, adjustedProfit }
  }, [taxComp, profitBeforeTax])

  const update = (field: keyof CT600TaxCompForm, value: string) => {
    onTaxCompChange({ ...taxComp, [field]: value })
  }

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-xl font-bold text-gray-900'>Tax Adjustments</h2>
        <p className='mt-1 text-sm text-gray-600'>
          Adjust the accounting profit to arrive at the taxable profit for
          corporation tax purposes.
        </p>
      </div>

      {/* Starting Point */}
      <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
        <div className='flex items-center justify-between'>
          <span className='text-sm font-medium text-gray-700'>
            Profit Before Tax (from P&L)
          </span>
          <span className='text-base font-bold text-gray-900'>
            £{formatAmount(profitBeforeTax)}
          </span>
        </div>
      </div>

      {/* Add-backs */}
      <div>
        <h3 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
          Add Back to Profit
        </h3>
        <p className='mb-4 text-xs text-gray-500'>
          Items charged in the accounts that are not allowable for tax purposes.
        </p>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <CurrencyInput
            label='Depreciation'
            value={taxComp.depreciation}
            onChange={v => update('depreciation', v)}
            helpText='Accounting depreciation (replaced by capital allowances)'
          />
          <CurrencyInput
            label='Disallowable Entertainment'
            value={taxComp.entertainmentDisallowable}
            onChange={v => update('entertainmentDisallowable', v)}
            helpText='Client entertainment and non-allowable hospitality'
          />
          <CurrencyInput
            label='Any Other Add-backs'
            value={taxComp.otherAddBacks}
            onChange={v => update('otherAddBacks', v)}
            helpText='Other items charged in the accounts not allowable for tax (e.g. fines, certain provisions, gifts not deductible)'
          />
          <CurrencyInput
            label='Total Add-backs'
            value={calc.addBacks.toFixed(2)}
            onChange={() => {}}
            readOnly
            highlight
          />
        </div>
      </div>

      {/* Deductions */}
      <div>
        <h3 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
          Deduct from Profit
        </h3>
        <p className='mb-4 text-xs text-gray-500'>
          Tax relief items that reduce the taxable profit.
        </p>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <CurrencyInput
            label='Capital Allowances'
            value={taxComp.capitalAllowances}
            onChange={v => update('capitalAllowances', v)}
            helpText='Annual investment allowance, writing-down allowances, etc.'
          />
          <CurrencyInput
            label='Bank Interest (already taxed)'
            value={taxComp.bankInterest}
            onChange={v => update('bankInterest', v)}
            helpText='Bank interest received that has already been taxed at source'
          />
          <CurrencyInput
            label='Unused Trading Losses from Previous Years'
            value={taxComp.lossesBroughtForward}
            onChange={v => update('lossesBroughtForward', v)}
            helpText='Unused trading losses from previous years available to offset against current profits'
          />
          <CurrencyInput
            label='Charitable Donations'
            value={taxComp.charitableDonations}
            onChange={v => update('charitableDonations', v)}
            helpText='Qualifying charitable donations (Gift Aid)'
          />
          <CurrencyInput
            label='Any Other Deductions'
            value={taxComp.otherDeductions}
            onChange={v => update('otherDeductions', v)}
            helpText='Enter any other deductions not already included above.'
          />
          <CurrencyInput
            label='Total Deductions'
            value={calc.deductions.toFixed(2)}
            onChange={() => {}}
            readOnly
            highlight
          />
        </div>
      </div>

      {/* Adjusted Profit */}
      <div className='rounded-lg border-2 border-blue-200 bg-blue-50 p-4'>
        <div className='flex items-center justify-between'>
          <div>
            <span className='text-base font-semibold text-blue-900'>
              Adjusted Profit for Tax
            </span>
            <p className='text-xs text-blue-700 mt-0.5'>
              Trading profit (after add-backs &amp; capital allowances) +
              interest income − losses brought forward − donations
            </p>
          </div>
          <span className='text-xl font-bold text-blue-900'>
            £{formatAmount(calc.adjustedProfit)}
          </span>
        </div>
      </div>
    </div>
  )
}
